// src/components/notes/NotesSidebar.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NoteList from './NoteList';
import NoteComposer from './NoteComposer';
import '../../styles/CommentSidebar.css';
import { notesApi } from '../../utils/readerApi';

export default function NotesSidebar({
  open,
  onClose,
  bookId: bookIdProp,

  // quote-picking hooks passed from BookReader (same as CommentSidebar)
  onBeginQuotePicking,       // () => void
  onPickQuote,               // () => Promise<{text,page,rects}>
  onEndQuotePicking,         // () => void
  onRegisterQuoteAppend,     // (fn) => void
  currentPage,               // number (optional; for filtering, if desired)
}) {
  const [notes, setNotes] = useState([]);
  const [pagesWithNotes, setPagesWithNotes] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [err, setErr] = useState('');

  const { bookId: ctxBookId } = (window.__readerContext || {});
  const bookId = bookIdProp || ctxBookId;

  // Close on ESC when open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Pages with (Fabric) notes summary
  useEffect(() => {
    if (!open || !bookId) return;
    let alive = true;
    (async () => {
      try {
        setLoadingPages(true); setErr('');
        const { pages } = await notesApi.summary({ bookId });
        if (!alive) return;
        setPagesWithNotes(Array.isArray(pages) ? pages : []);
      } catch (e) {
        if (!alive) return;
        setErr(typeof e === 'string' ? e : (e?.message || 'Failed to load notes summary'));
      } finally {
        if (alive) setLoadingPages(false);
      }
    })();
    return () => { alive = false; };
  }, [open, bookId]);

  // Load note items (you can choose to filter by currentPage or not)
  useEffect(() => {
    if (!open || !bookId) return;
    let alive = true;
    (async () => {
      try {
        setLoadingNotes(true); setErr('');
        // If you prefer not to filter: omit page
        const { notes: list } = await notesApi.list({ bookId /*, page: currentPage */ });
        if (!alive) return;
        setNotes(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!alive) return;
        setErr(typeof e === 'string' ? e : (e?.message || 'Failed to load notes'));
      } finally {
        if (alive) setLoadingNotes(false);
      }
    })();
    return () => { alive = false; };
  }, [open, bookId, currentPage]);

  const addNote = async (note) => {
    if (!bookId) return;
    const payload = {
      bookId,
      text: note.text,
      quote: note.quote || '',
      page: note.page ?? null,
      rects: Array.isArray(note.rects) && note.rects.length ? note.rects : null,
    };
    const { id } = await notesApi.create(payload);
    setNotes(prev => [{ id, ...note }, ...prev]);
  };

  const deleteNote = async (note) => {
    if (!bookId || !note?.id) return;
    await notesApi.remove({ bookId, noteId: note.id });
    setNotes(prev => prev.filter(n => n.id !== note.id));
  };

  return (
    <div className={`comment-sidebar note-sidebar ${open ? 'open' : ''}`} aria-hidden={!open}>
      <div className="sidebar-header">
        <h3>My Notes</h3>
        <div className="sidebar-actions">
          <IconButton
            aria-label="Close notes"
            onClick={onClose}
            className="sidebar-close-btn"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="sidebar-subheader" style={{ padding: '0 12px 8px', fontSize: 13, color: '#6b6b6b' }}>
        {loadingPages && 'Loading pages with notes…'}
        {!loadingPages && err && <span style={{ color: '#b00020' }}>{err}</span>}
        {!loadingPages && !err && (
          pagesWithNotes.length > 0
            ? <>Pages with notes (drawing): <strong>{pagesWithNotes.join(', ')}</strong></>
            : <>No saved drawing notes yet.</>
        )}
      </div>

      {loadingNotes ? (
        <div style={{ padding: 12, fontSize: 13, color: '#6b6b6b' }}>Loading notes…</div>
      ) : (
        <NoteList notes={notes} onDelete={deleteNote} />
      )}

      <NoteComposer
        onAdd={addNote}
        onBeginQuotePicking={onBeginQuotePicking}
        onRegisterQuoteAppend={onRegisterQuoteAppend}
      />
    </div>
  );
}
